import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Settings } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { UserAvatar } from '@/components/UserAvatar';

export function AccountMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const displayName = user?.name || user?.email?.split('@')[0] || 'Account';

  const handleSettings = () => {
    setIsOpen(false);
    navigate('/settings');
  };

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Open account menu"
        className="cursor-pointer rounded-full outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2"
      >
        <UserAvatar email={user?.email} name={user?.name} pictureUrl={user?.pictureUrl} />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
        >
          <div className="flex items-center gap-3 px-4 py-4">
            <UserAvatar
              email={user?.email}
              name={user?.name}
              pictureUrl={user?.pictureUrl}
              className="size-10"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">{displayName}</p>
              {user?.email && (
                <p className="truncate text-xs text-slate-500">{user.email}</p>
              )}
            </div>
          </div>

          <div className="border-t border-gray-100 py-1">
            <button
              type="button"
              role="menuitem"
              onClick={handleSettings}
              className="flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-gray-100 hover:text-slate-900"
            >
              <Settings className="size-4 text-slate-500" />
              Settings
            </button>
          </div>

          <div className="border-t border-gray-100 py-1">
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-gray-100 hover:text-slate-900"
            >
              <LogOut className="size-4 text-slate-500" />
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
